import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, RefreshCw, User, Trash2, Bot,
  Clock, MessageSquare, AlertCircle, Play, Pause, Send,
  Sparkles,
  FileText, 
  CircleDot, CircleOff, CircleSlash, Info, Database,
  MoreVertical, Zap, Hash, ChevronDown, Users
} from 'lucide-react';
import { chatService, legacyService } from '../../services/PlatformService';
import axios from 'axios';
import { useAuth } from '../../components/auth/AuthContext';
import { format } from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';
import { useAdminMessagePolling } from '../../hooks/agent/useAdminMessagePolling';
import ChatMessages from '../../components/chat/ChatMessages';
import ChatInput from '../../components/chat/ChatInput';
import { processMessageContent, getFilesByMimeType } from '../../lib/mediaUtils';
import { lightenDarkenColor, getContrastColor } from '../../lib/colorUtils';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const AgentChatDetail = () => {
  const { id: chatId } = useParams();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const { currentUser } = useAuth();
  
  // State for conversation and messages
  const [chat, setConversation] = useState(null);
  const [agent, setAgent] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  const [showClientInfo, setShowClientInfo] = useState(false);
  
  const [userInfo, setUserInfo] = useState(null);
  const [conversationTone, setConversationTone] = useState('');
  const [interests, setInterests] = useState([]);
  
  // States for Human in Loop
  const [conversationState, setConversationState] = useState('active');
  const [isOwner, setIsOwner] = useState(true);
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [pauseReason, setPauseReason] = useState('');
  const [isUpdatingState, setIsUpdatingState] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  
  // NOVO: Estados para o modal de encerramento
  const [showEndModal, setShowEndModal] = useState(false);
  const [endMessage, setEndMessage] = useState('CineLaser agradece seu contato');

  const handleNewMessagesFromPolling = useCallback((result) => {
    console.log('[Admin] Processando novas mensagens do polling');
    
    let messagesLlm = [];
    
    for (let msg of result.messages) {
      const agentMessage = msg["agent_msg"];

      if (agentMessage.role === "tool" || agentMessage?.tool_calls !== undefined) {
        messagesLlm.push(agentMessage);
      }

      if (agentMessage.role === "assistant" || agentMessage.role === "user") {
        const { audioUrls, imageUrls, documentUrls, videoUrls } = getFilesByMimeType(msg?.metadata?.files || []);

        if (agentMessage?.content !== undefined) {
          const { content: processedContent } = processMessageContent(agentMessage.content || '');

          messagesLlm.push({
            role: agentMessage.role,
            content: processedContent || agentMessage.content,
            imageUrls,
            audioUrls,
            documentUrls,
            videoUrls,
            files: msg?.metadata?.files || [],
            timestamp: msg.created_at,
            metadata: msg?.metadata
          });
        }
      }
    }
    
    setMessages(messagesLlm);
  }, []);

  const { syncNow } = useAdminMessagePolling(
    chatId,
    conversationState,
    handleNewMessagesFromPolling
  );

  // MODIFICADO: Função para abrir o modal de encerramento
  const handleEndConversation = () => {
    setShowEndModal(true);
  };

  // NOVO: Função para confirmar o encerramento e enviar mensagem
  const confirmEndConversation = async () => {
    try {
      // Primeiro, enviar a mensagem de despedida se houver texto
      if (endMessage.trim()) {
        const tempMessage = {
          id: `temp-${Date.now()}`,
          role: 'assistant',
          content: endMessage,
          timestamp: new Date().toISOString(),
          metadata: { human_in_loop: true, farewell_message: true }
        };
        
        setMessages(prev => [...prev, tempMessage]);

        const token = localStorage.getItem('authToken');
        
        await axios.post(
          `${API_BASE_URL}/chats/${chatId}/human-message`,
          { content: endMessage, files: [] },
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
      }

      // Aguardar um momento para garantir que a mensagem foi enviada
      await new Promise(resolve => setTimeout(resolve, 500));

      // Então, encerrar a conversa
      await updateConversationState('ended', 'Conversa encerrada pelo administrador');
      
      setShowEndModal(false);
      setEndMessage('CineLaser agradece seu contato'); // Reset para o valor padrão
    } catch (error) {
      console.error('Erro ao encerrar conversa:', error);
      alert('Erro ao enviar mensagem de despedida. Tente novamente.');
    }
  };

  const handleHandoffConversation = async () => {
    const reason = prompt('Motivo do handoff (opcional):');
    await updateConversationState('handoff', reason || 'Transferido para atendimento humano');
  };

  const getClientInfo = () => {
    const metadata = chat.metadata || {};
    const name = metadata.user_name || metadata.client_name || metadata?.custom_field?.nome || metadata.nome || chat.title || 'Cliente Sem Nome';
    const photoUrl = metadata.photo_url || metadata.avatar_url || metadata.foto || null;

    return { name, photoUrl };
  };

  // Function to fetch conversation data and messages
  const fetchConversationData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
        // Fetch conversation data
        const conversationData = await chatService.getChatById(chatId);
        setConversation(conversationData);
        
        // Extract metadata if available
        if (conversationData.metadata) {
            if (conversationData.metadata.tone) {
              setConversationTone(conversationData.metadata.tone);
            }
            
            if (conversationData.metadata.interests && Array.isArray(conversationData.metadata.interests)) {
              setInterests(conversationData.metadata.interests);
            }
            
            if (conversationData.metadata.user_info) {
              setUserInfo(conversationData.metadata.user_info);
            }
        }

        fetchAgentData(conversationData.agent_id);
      
        const result = await chatService.getChatMessages(conversationData.id);

        let messagesLlm = [];
        
        for (let msg of result.messages) {
            const agentMessage = msg["agent_msg"]

            if (agentMessage.role === "tool" || agentMessage?.tool_calls !== undefined) {
                messagesLlm.push(agentMessage)
            }

            if (agentMessage.role === "assistant" || agentMessage.role === "user") {
                const { audioUrls, imageUrls, documentUrls, videoUrls } = getFilesByMimeType(msg?.metadata?.files || []);

                if (agentMessage?.content !== undefined) {
                    const { content: processedContent } = processMessageContent(agentMessage.content || '');

                    messagesLlm.push({
                        role: agentMessage.role,
                        content: processedContent || agentMessage.content,
                        imageUrls,
                        audioUrls,
                        documentUrls,
                        videoUrls,
                        files: msg?.metadata?.files || [],
                        timestamp: msg.created_at,
                        metadata: msg?.metadata
                    })
                }
            }
        }
        
        setMessages(messagesLlm);
    } catch (err) {
      console.error('Error fetching chat data:', err);
      setError('Ops! Não conseguimos carregar a conversa. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteMessages = async () => {
    if (window.confirm('Tem certeza que deseja apagar TODAS as mensagens desta conversa? Esta ação não pode ser desfeita.')) {
      setIsLoading(true);
      setError(null);
      
      try {
          await legacyService.clearChatHistory(chat?.id);
          alert('Todas as mensagens foram apagadas!');
          window.location.reload();
      } catch (err) {
        console.error('Error deleting chat messages:', err);
        alert('Erro ao apagar mensagens. Tente novamente.');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const fetchAgentData = async (agentId) => {
      try {
        setIsLoading(true);
        setError(null);
        
        const token = localStorage.getItem('authToken');
        const headers = {};
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        
        const response = await axios.get(`${API_BASE_URL}/agents/${agentId}`, { headers });
        
        if (response.data && response.data.data) {
          setAgent(response.data.data);
          
          if (currentUser && response.data.data.created_by === currentUser.id) {
            setIsOwner(true);
          }
        } else {
          throw new Error("Invalid response format");
        }
      } catch (err) {
        console.error("Error fetching agent data:", err);
        if (err.response) {
          if (err.response.status === 404) {
            setError("Funcionário não encontrado.");
          } else if (err.response.status === 403) {
            setError("Você não tem permissão para acessar este funcionário.");
          } else {
            setError(err.response.data?.error || "Erro ao carregar o funcionário");
          }
        } else {
          setError("Erro de conexão. Verifique sua internet e tente novamente.");
        }
      } finally {
        setIsLoading(false);
      }
    };
  
  // Function to fetch conversation state
  const fetchConversationState = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(
        `${API_BASE_URL}/chats/${chatId}/state`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.data) {
        setConversationState(response.data.state);
        setIsOwner(true);
      }
    } catch (error) {
      console.error('Error fetching conversation state:', error);
    }
  };

  // Function to update conversation state
  const updateConversationState = async (newState, reason = '') => {
    setIsUpdatingState(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.put(
        `${API_BASE_URL}/chats/${chatId}/state`,
        { state: newState, reason },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.data) {
        setConversationState(response.data.new_state);
        setShowPauseModal(false);
        setPauseReason('');
        
        // Add system message to chat
        const systemMessage = {
          id: `system-${Date.now()}`,
          role: 'system',
          content: `Conversa ${newState === 'paused' ? 'pausada' : newState === 'active' ? 'reativada' : 'finalizada'}${reason ? `: ${reason}` : ''}`,
          timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, systemMessage]);
      }
    } catch (error) {
      console.error('Error updating conversation state:', error);
      alert('Erro ao atualizar estado da conversa');
    } finally {
      setIsUpdatingState(false);
    }
  };

  // Function to send manual message (Human in Loop)
  const sendHumanMessage = async () => {
    if (!inputMessage.trim() && selectedDocuments.length === 0) return;

    const userMessageContent = inputMessage;
    setInputMessage('');
    
    // Add temporary message to chat
    const tempMessage = {
      id: `temp-${Date.now()}`,
      role: 'assistant',
      content: userMessageContent,
      timestamp: new Date().toISOString(),
      metadata: { human_in_loop: true },
      files: selectedDocuments
    };
    
    setMessages(prev => [...prev, tempMessage]);
    setSelectedDocuments([]);

    try {
      const token = localStorage.getItem('authToken');
      
      const files = selectedDocuments.map(doc => ({
        filename: doc.file_url.split('/').pop(),
        content_type: doc.mime_type,
        data: ''
      }));

      const response = await axios.post(
        `${API_BASE_URL}/chats/${chatId}/human-message`,
        { 
          content: userMessageContent,
          files: files
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.data && response.data.message) {
        setMessages(prev => prev.map(msg => 
          msg.id === tempMessage.id 
            ? { 
                ...response.data.message.agent_msg, 
                id: response.data.message.id,
                timestamp: response.data.message.created_at,
                metadata: response.data.message.metadata,
                files: selectedDocuments
              }
            : msg
        ));
      }
    } catch (error) {
      console.error('Error sending manual message:', error);
      setMessages(prev => prev.filter(msg => msg.id !== tempMessage.id));
      alert('Erro ao enviar mensagem. Por favor, tente novamente.');
    }
  };
  
  // Function to send message (modified for Human in Loop)
  const handleSendMessage = (documents = []) => {
    setSelectedDocuments(documents);
    
    if (conversationState === 'ended') {
      alert('Esta conversa foi finalizada e não aceita mais mensagens.');
      return;
    }

    if (isOwner) {
      if (conversationState !== 'paused') {
        updateConversationState('paused')
      }
      sendHumanMessage();
    } else if (conversationState === 'paused') {
      alert('Esta conversa está pausada. Apenas o administrador pode enviar mensagens.');
    } else if (conversationState === 'ended') {
      alert('Esta conversa está encerrada.');
    } else {
      alert("A interceptação humana de mensagens está em construção. Sua mensagem não será enviada.");
    }
  };
  
  // Load data when component mounts
  useEffect(() => {
    if (chatId) {
      fetchConversationData();
      fetchConversationState();
    }
  }, [chatId]);
  
  // Scroll to end of messages when loading or new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Apply dynamic styles based on agent widget settings
  useEffect(() => {
      if (!agent || !agent.widget_settings) return;
      
      const widgetSettings = agent.widget_settings;

      const styleElement = document.createElement('style');
      styleElement.textContent = `
        @import url('https://fonts.googleapis.com/css2?family=${widgetSettings.font_primary?.replace(' ', '+') || 'Inter'}:wght@400;500;600;700&family=${widgetSettings.font_secondary?.replace(' ', '+') || 'Roboto'}:wght@400;500&display=swap');
        
        .agent-chat-container {
          --primary-color: ${widgetSettings.primary_color || '#A020F0'};
          --secondary-color: ${widgetSettings.secondary_color || '#E0E7FF'};
          --primary-font: '${widgetSettings.font_primary || 'Inter'}', sans-serif;
          --secondary-font: '${widgetSettings.font_secondary || 'Roboto'}', sans-serif;
        }
        
        .agent-primary-button {
          background-color: var(--primary-color);
        }
        
        .agent-primary-button:hover {
          background-color: ${lightenDarkenColor(widgetSettings.primary_color || '#A020F0', -20)};
        }
        
        .agent-primary-bg {
          background-color: var(--primary-color);
        }
        
        .agent-secondary-bg {
          background-color: var(--secondary-color);
        }
        
        .agent-primary-text {
          color: var(--primary-color);
        }
        
        .agent-primary-border {
          border-color: var(--primary-color);
        }
        
        .agent-message-bubble {
          background-color: var(--secondary-color);
          color: ${getContrastColor(widgetSettings.secondary_color || '#E0E7FF')};
        }
      `;
      
      document.head.appendChild(styleElement);
      
      return () => {
        document.head.removeChild(styleElement);
      };
    }, [agent]);
  
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    
    try {
      const date = new Date(timestamp);
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${hours}:${minutes}`;
    } catch (e) {
      console.error('Error formatting date:', e);
      return '';
    }
  };
  
  const formatFriendlyDate = (timestamp) => {
    if (!timestamp) return '';
    
    try {
      const date = new Date(timestamp);
      return format(date, "d 'de' MMMM 'às' HH:mm", { locale: ptBR });
    } catch (e) {
      return '';
    }
  };

  // Get channel icon
  const getChannelIcon = (channel) => {
    console.log(channel)
    switch (channel) {
      case 'whatsapp':
        return (
          <img 
            src={"https://img.icons8.com/?size=512&id=16713&format=png"} 
            alt="WhatsApp" 
            className="w-5 h-5"
          />
        );
      case 'instagram_direct':
        return (
          <img 
            src={"https://upload.wikimedia.org/wikipedia/commons/thumb/e/e7/Instagram_logo_2016.svg/2000px-Instagram_logo_2016.svg.png"} 
            alt="instagram_direct" 
            className="w-5 h-5"
          />
        );
      case 'messenger':
        return (
          <img 
            src={"https://upload.wikimedia.org/wikipedia/commons/b/be/Facebook_Messenger_logo_2020.svg"} 
            alt="messenger" 
            className="w-5 h-5"
          />
        );
      case 'integration':
        return (
          <div className="w-6 h-6 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
            <MessageSquare className="w-4 h-4 text-white" />
          </div>
        );
      case 'platform':
        return <div className="w-6 h-6 bg-gradient-to-br from-indigo-500 to-blue-500 rounded-lg flex items-center justify-center">
            <MessageSquare className="w-4 h-4 text-white" />
          </div>
      default:
        return <MessageSquare className="w-5 h-5 text-gray-500" />;
    }
  };

  // Get tone icon
  const getToneIcon = (tone) => {
    if (tone == "") return <></>
    const tones = {
      'frustrado': { icon: AlertCircle, color: 'text-red-600' },
      'irritado': { icon: AlertCircle, color: 'text-orange-600' },
      'confuso': { icon: Info, color: 'text-yellow-600' },
      'neutro': { icon: CircleDot, color: 'text-gray-600' },
      'satisfeito': { icon: Sparkles, color: 'text-green-600' },
      'entusiasmado': { icon: Zap, color: 'text-blue-600' }
    };
    const ToneIcon = tones[tone]?.icon || CircleDot;
    const color = tones[tone]?.color || 'text-gray-600';
    
    return <ToneIcon className={`w-4 h-4 ${color}`} />;
  };

  // Format metadata for display
  const formatMetadataValue = (key, value) => {
    if (key === 'projeto_id' || key === 'project_id') {
      return { label: 'ID do Projeto', icon: FileText, highlight: false };
    }
    if (key === 'client_id' || key === 'cliente_id') {
      return { label: 'ID do Cliente', icon: Hash, highlight: false };
    }
    if (key === 'user_name' || key === 'client_name' || key === 'nome') {
      return { label: 'Nome do Cliente', icon: User, highlight: true };
    }
    
    // Formatar key genérica
    const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    return { label, icon: Info, highlight: false };
  };
  
  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-blue-50">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-indigo-100 to-green-100 rounded-3xl mb-4">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
          </div>
          <p className="text-xl text-gray-600">Carregando conversa...</p>
        </div>
      </div>
    );
  }
  
  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex flex-col bg-gradient-to-br from-indigo-50 via-white to-blue-50 items-center justify-center p-4">
        <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl p-12 text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-gray-900 mb-2">Ops! Algo deu errado</h3>
          <p className="text-lg text-gray-600 mb-6">{error}</p>
          <div className="flex gap-4 justify-center">
            <button
              onClick={() => navigate(-1)}
              className="px-6 py-3 bg-gray-200 text-gray-800 rounded-xl hover:bg-gray-300 font-semibold"
            >
              Voltar
            </button>
            <button
              onClick={fetchConversationData}
              className="px-6 py-3 bg-gradient-to-r from-green-800 to-green-600 text-white rounded-xl hover:from-green-700 hover:to-green-700 font-semibold"
            >
              Tentar novamente
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  // No chat found state
  if (!chat) {
    return (
      <div className="min-h-screen flex flex-col bg-gradient-to-br from-indigo-50 via-white to-blue-50 items-center justify-center p-4">
        <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl p-12 text-center">
          <MessageSquare className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-gray-900 mb-2">Conversa não encontrada</h3>
          <p className="text-lg text-gray-600 mb-6">Não foi possível encontrar esta conversa.</p>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-3 bg-gradient-to-r from-green-800 to-green-600 text-white rounded-xl hover:from-green-700 hover:to-green-700 font-semibold"
          >
            Voltar
          </button>
        </div>
      </div>
    );
  }

  const widgetSettings = agent?.widget_settings || {};
  const { name: clientName, photoUrl: clientPhotoUrl } = getClientInfo();

  return (
    <div className="flex h-screen bg-gradient-to-br from-indigo-50 via-white to-blue-50 agent-chat-container">
      {/* Sidebar Lateral com Informações */}
<div className="w-96 bg-white/80 backdrop-blur-sm border-r-2 border-indigo-100 flex flex-col overflow-hidden">
  {/* Header do Cliente - Fixed */}
  <div className="flex-shrink-0 p-6 bg-gradient-to-r from-green-800 to-green-600 text-white">
    <div className="flex items-center gap-4">
      <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center overflow-hidden flex-shrink-0">
        {clientPhotoUrl ? (
          <img src={clientPhotoUrl} alt={clientName || 'Cliente'} className="w-full h-full object-cover" />
        ) : (
          <User className="w-8 h-8 text-white" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <h2 className="text-xl font-bold truncate" title={clientName || 'Cliente Sem Nome'}>
          {clientName || 'Cliente Sem Nome'}
        </h2>
        <p className="text-sm opacity-90 truncate" title={chat.title || `Conversa #${chat.id.substring(0, 8)}`}>
          {chat.title || `Conversa #${chat.id.substring(0, 8)}`}
        </p>
        <div className="flex items-center gap-2 mt-1 text-sm opacity-90">
          {getChannelIcon(chat.channel)}
          <span>{chat.channel === 'whatsapp' ? 'WhatsApp' : chat.channel === 'instagram_direct' ? 'Instagram Direct' : chat.channel === 'integration' ? 'Instagram' : chat.channel === 'messenger' ? 'Facebook Messenger' :'Plataforma'}</span>
          {conversationTone && (
            <span className="flex items-center gap-1">
              {getToneIcon(conversationTone)}
              {conversationTone}
            </span>
          )}
        </div>
      </div>
    </div>
    
    {/* Interesses */}
    {interests.length > 0 && (
      <div className="mt-4 flex flex-wrap gap-2">
        {interests.map((interest, index) => (
          <span 
            key={index} 
            className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium"
          >
            {interest}
          </span>
        ))}
      </div>
    )}
  </div>
  
  {/* Conteúdo Scrollável */}
  <div className="flex-1 overflow-y-auto">
    <div className="p-6 space-y-4">
      {/* Funcionário Responsável */}
      {agent && (
        <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-2xl shadow-sm p-5">
          <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
            <Bot className="w-5 h-5 text-green-600" />
            Funcionário Responsável
          </h3>
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-2xl overflow-hidden bg-gradient-to-br from-indigo-100 to-green-100 p-0.5 flex-shrink-0">
              <div className="w-full h-full rounded-2xl overflow-hidden bg-white flex items-center justify-center">
                {agent.widget_settings?.logo_url ? (
                  <img 
                    src={agent.widget_settings.logo_url} 
                    alt={agent.name} 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Bot className="w-10 h-10 text-indigo-400" />
                )}
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-lg text-gray-900 truncate">{agent.name}</p>
              <p className="text-sm text-gray-600">Atendente Digital 24/7</p>
              <div className="mt-1 flex items-center gap-1">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-xs text-gray-500">Sempre disponível</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Status do Atendimento */}
      <div className="bg-white rounded-2xl shadow-sm p-5">
        <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-green-600" />
          Status do Atendimento
        </h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
            <span className="text-gray-600 flex items-center gap-2">
              <CircleDot className="w-4 h-4" />
              Estado
            </span>
            <span className={`px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1 ${
              conversationState === 'active' 
                ? 'bg-green-100 text-green-700' 
                : conversationState === 'paused'
                ? 'bg-yellow-100 text-yellow-700'
                : 'bg-red-100 text-red-700'
            }`}>
              {conversationState === 'active' && <><CircleDot className="w-3 h-3" /> Ativo</>}
              {conversationState === 'paused' && <><CircleOff className="w-3 h-3" /> Pausado</>}
              {conversationState === 'ended' && <><CircleSlash className="w-3 h-3" /> Finalizado</>}
            </span>
          </div>
          
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
            <span className="text-gray-600 flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Iniciado
            </span>
            <span className="text-gray-800 text-sm">
              {formatFriendlyDate(chat.created_at)}
            </span>
          </div>
        </div>
      </div>

      {/* Informações do Cliente - Minimizado */}
      {chat.metadata && Object.keys(chat.metadata).length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <button
            onClick={() => setShowClientInfo(!showClientInfo)}
            className="w-full p-5 flex items-center justify-between hover:bg-gray-50 transition-colors"
          >
            <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
              <Database className="w-5 h-5 text-green-600" />
              Informações do Cliente
            </h3>
            <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${showClientInfo ? 'rotate-180' : ''}`} />
          </button>
          
          {showClientInfo && (
            <div className="px-5 pb-5 space-y-3 border-t">
              {Object.entries(chat?.metadata?.custom_field !== undefined ? chat?.metadata?.custom_field : chat?.metadata)
                .filter(([key]) => !['tone', 'interests', 'user_info', 'photo_url'].includes(key))
                .map(([key, value], index) => {
                  const { label, icon: Icon, highlight } = formatMetadataValue(key, value);
                  
                  return (
                    <div 
                      key={index} 
                      className={`flex items-center justify-between mt-6 p-3 rounded-xl ${
                        highlight ? 'bg-indigo-50 border border-indigo-200' : 'bg-gray-50'
                      }`}
                    >
                      <span className={`flex items-center gap-2 text-sm ${
                        highlight ? 'text-green-700 font-medium' : 'text-gray-600'
                      }`}>
                        <Icon className="w-4 h-4 flex-shrink-0" />
                        <span className="truncate">{label}</span>
                      </span>
                      <span className={`text-sm truncate ml-2 ${
                        highlight ? 'text-indigo-900 font-semibold' : 'text-gray-800'
                      }`} title={typeof value === 'object' ? JSON.stringify(value) : String(value)}>
                        {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                      </span>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      )}
    </div>
  </div>
  
{isOwner && (
  <div className="flex-shrink-0 p-6 bg-gradient-to-r from-indigo-50 to-blue-50 border-t-2 border-indigo-100">
    <div className="space-y-4">
      {conversationState === 'active' ? (
        <div className="bg-white rounded-2xl p-5 shadow-lg border-2 border-indigo-200 space-y-3">
          <h4 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
            <CircleDot className="w-5 h-5 text-green-600" />
            Atendimento Automático Ativo
          </h4>
          
          <button
            onClick={() => setShowPauseModal(true)}
            disabled={isUpdatingState}
            className="w-full py-3 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white rounded-xl font-bold transition-all transform hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2 shadow-lg"
          >
            <Pause className="w-5 h-5" />
            Pausar Automático
          </button>
          
          <button
            onClick={handleHandoffConversation}
            disabled={isUpdatingState}
            className="w-full py-3 bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white rounded-xl font-bold transition-all transform hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2 shadow-lg"
          >
            <Users className="w-5 h-5" />
            Transferir para Humano
          </button>
          
          <button
            onClick={handleEndConversation}
            disabled={isUpdatingState}
            className="w-full py-3 bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white rounded-xl font-bold transition-all transform hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2 shadow-lg"
          >
            <CircleSlash className="w-5 h-5" />
            Encerrar Conversa
          </button>
        </div>
      ) : conversationState === 'paused' ? (
        <div className="space-y-3">
          <div className="bg-yellow-100 border-2 border-yellow-300 rounded-2xl p-5">
            <div className="flex items-center gap-2 text-yellow-800 mb-2">
              <Send className="w-6 h-6" />
              <span className="font-bold text-lg">Atendimento Manual Ativo</span>
            </div>
            <p className="text-sm text-yellow-700">
              Você está respondendo manualmente. O funcionário digital está em pausa.
            </p>
          </div>
          
          <button
            onClick={() => updateConversationState('active')}
            disabled={isUpdatingState}
            className="w-full py-3 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white rounded-xl font-bold transition-all transform hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2 shadow-lg"
          >
            <Play className="w-5 h-5" />
            Transferir para o Agente
          </button>
          
          <button
            onClick={handleEndConversation}
            disabled={isUpdatingState}
            className="w-full py-3 bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white rounded-xl font-bold transition-all transform hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2 shadow-lg"
          >
            <CircleSlash className="w-5 h-5" />
            Encerrar Conversa
          </button>
        </div>
      ) : conversationState === 'handoff' ? (
        <div className="space-y-3">
          <div className="bg-blue-100 border-2 border-blue-300 rounded-2xl p-5">
            <div className="flex items-center gap-2 text-blue-800 mb-2">
              <Users className="w-6 h-6" />
              <span className="font-bold text-lg">Aguardando Atendimento</span>
            </div>
            <p className="text-sm text-blue-700">
              Conversa transferida para atendimento humano.
            </p>
          </div>
          
          <button
            onClick={() => setShowPauseModal(true)}
            disabled={isUpdatingState}
            className="w-full py-3 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white rounded-xl font-bold transition-all transform hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2 shadow-lg"
          >
            <Pause className="w-5 h-5" />
            Pausar Agente
          </button>
          
          <button
            onClick={handleEndConversation}
            disabled={isUpdatingState}
            className="w-full py-3 bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white rounded-xl font-bold transition-all transform hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2 shadow-lg"
          >
            <CircleSlash className="w-5 h-5" />
            Encerrar Conversa
          </button>
        </div>
      ) : (
        <div className="bg-red-100 border-2 border-red-300 rounded-2xl p-5">
          <div className="flex items-center gap-2 text-red-800">
            <CircleSlash className="w-6 h-6" />
            <span className="font-bold text-lg">Atendimento Finalizado</span>
          </div>
          <p className="text-sm text-red-700 mt-1">
            Esta conversa foi encerrada e não aceita novas mensagens.
          </p>
        </div>
      )}
    </div>
  </div>
)}
</div>

      {/* NOVO: Modal de Encerramento com Mensagem de Despedida */}
      {showEndModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-2xl flex items-center justify-center">
                <CircleSlash className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Encerrar Conversa</h3>
            </div>
            
            <p className="text-lg text-gray-600 mb-4">
              Deseja enviar uma mensagem de despedida antes de encerrar?
            </p>
            
            <textarea
              className="w-full border-2 border-gray-200 rounded-xl p-4 mb-6 text-base focus:ring-4 focus:ring-red-100 focus:border-red-500 transition-all"
              rows="4"
              placeholder="Mensagem de despedida"
              value={endMessage}
              onChange={(e) => setEndMessage(e.target.value)}
            />
            
            <div className="flex gap-3">
              <button
                onClick={confirmEndConversation}
                disabled={isUpdatingState}
                className="flex-1 py-3 bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white rounded-xl font-bold disabled:opacity-50 transition-all transform hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2"
              >
                {isUpdatingState ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Encerrando...
                  </>
                ) : (
                  <>
                    <CircleSlash className="w-5 h-5" />
                    Enviar e Encerrar
                  </>
                )}
              </button>
              <button
                onClick={() => {
                  setShowEndModal(false);
                  setEndMessage('CineLaser agradece seu contato'); // Reset
                }}
                disabled={isUpdatingState}
                className="flex-1 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-xl font-bold transition-all transform hover:scale-[1.02] active:scale-[0.98]"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pause Modal */}
      {showPauseModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-yellow-100 rounded-2xl flex items-center justify-center">
                <Pause className="w-6 h-6 text-yellow-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Pausar Atendimento</h3>
            </div>
            
            <p className="text-lg text-gray-600 mb-6">
              O funcionário digital parará de responder automaticamente. Você poderá enviar mensagens manualmente.
            </p>
            
            <textarea
              className="w-full border-2 border-gray-200 rounded-xl p-4 mb-6 text-base focus:ring-4 focus:ring-green-100 focus:border-green-500 transition-all"
              rows="3"
              placeholder="Mensagem automática enquanto pausado (opcional)"
              value={pauseReason}
              onChange={(e) => setPauseReason(e.target.value)}
            />
            
            <div className="flex gap-3">
              <button
                onClick={() => updateConversationState('paused', pauseReason)}
                disabled={isUpdatingState}
                className="flex-1 py-3 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white rounded-xl font-bold disabled:opacity-50 transition-all transform hover:scale-[1.02] active:scale-[0.98]"
              >
                {isUpdatingState ? 'Pausando...' : 'Confirmar Pausa'}
              </button>
              <button
                onClick={() => {
                  setShowPauseModal(false);
                  setPauseReason('');
                }}
                disabled={isUpdatingState}
                className="flex-1 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-xl font-bold transition-all transform hover:scale-[1.02] active:scale-[0.98]"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Área Principal do Chat */}
      <div className="flex-1 flex flex-col bg-white">
        {/* Header do Chat */}
        <div className="bg-white border-b-2 border-gray-100 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate(-1)} 
                className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
              >
                <ArrowLeft className="w-6 h-6 text-gray-600" />
              </button>
              
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden">
                  {clientPhotoUrl ? (
                    <img src={clientPhotoUrl} alt={clientName || 'Cliente'} className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-6 h-6 text-gray-400" />
                  )}
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">
                    {clientName || 'Cliente Sem Nome'}
                  </h2>
                  <p className="text-sm text-gray-500 flex items-center gap-2">
                    {chat.title || `Conversa #${chat.id.substring(0, 8)}`}
                    <span>•</span>
                    {getChannelIcon(chat.channel)}
                    <span>{messages.length} mensagens</span>
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {conversationState === 'paused' && isOwner && (
                <div className="px-4 py-2 bg-yellow-100 text-yellow-800 rounded-xl text-sm font-semibold flex items-center gap-2">
                  <Send className="w-4 h-4" />
                  Modo Manual
                </div>
              )}
              
              <button 
                className="p-3 bg-gradient-to-r from-indigo-100 to-green-100 text-green-700 hover:from-indigo-200 hover:to-blue-200 rounded-xl font-semibold transition-all transform hover:scale-[1.05] active:scale-[0.98]"
                onClick={fetchConversationData}
                title="Atualizar conversa"
              >
                <RefreshCw className="w-5 h-5" />
              </button>

              {/* Options Menu */}
              <div className="relative">
                <button
                  onClick={() => setShowOptionsMenu(!showOptionsMenu)}
                  className="p-3 hover:bg-gray-100 rounded-xl transition-colors"
                >
                  <MoreVertical className="w-5 h-5 text-gray-600" />
                </button>
                
                {showOptionsMenu && (
                  <>
                    <div 
                      className="fixed inset-0 z-10" 
                      onClick={() => setShowOptionsMenu(false)}
                    />
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border z-20">
                      <button
                        onClick={() => {
                          setShowOptionsMenu(false);
                          handleDeleteMessages();
                        }}
                        className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-red-600 rounded-xl"
                      >
                        <Trash2 className="w-4 h-4" />
                        Limpar todas as mensagens
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Mensagens do Chat */}
        <ChatMessages
          agent={agent}
          messages={messages}
          isTyping={false}
          widgetSettings={widgetSettings}
          formatTimestamp={formatTimestamp} 
        />
      
        {/* Indicadores de Estado */}
        {(conversationState === 'paused' && !isOwner) && (
          <div className="bg-yellow-50 border-t-2 border-yellow-200 p-4">
            <div className="max-w-4xl mx-auto flex items-center justify-center gap-2 text-yellow-800">
              <CircleOff className="w-5 h-5" />
              <span className="font-semibold text-lg">
                Atendimento pausado. Aguarde o administrador.
              </span>
            </div>
          </div>
        )}
        
        {conversationState === 'ended' && (
          <div className="bg-red-50 border-t-2 border-red-200 p-4">
            <div className="max-w-4xl mx-auto flex items-center justify-center gap-2 text-red-800">
              <CircleSlash className="w-5 h-5" />
              <span className="font-semibold text-lg">
                Este atendimento foi finalizado.
              </span>
            </div>
          </div>
        )}
        
        {/* Input de Mensagem */}
        {conversationState !== 'ended' && (conversationState !== 'paused' || isOwner) && (
          <ChatInput 
            inputMessage={inputMessage} 
            setInputMessage={setInputMessage} 
            handleSendMessage={handleSendMessage} 
            isTyping={false} 
            widgetSettings={widgetSettings} 
          />
        )}
      </div>
    </div>
  );
};

export default AgentChatDetail;